import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Calendar, Flag, Shield, Check, Award, ChevronLeft, ChevronRight, Gift, Lock } from 'lucide-react';
import MembershipBadge from '../components/MembershipBadge';
import AgeBadge from '../components/AgeBadge';
import { FEATURES } from '../config/featureFlags';
import { getFirstName } from '../utils/nameUtils';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { isDark } = useTheme();
  const [user, setUser] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    fetchUser();
  }, [userId]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleMessage = () => {
    navigate(`/chat/${userId}`, { 
      state: { 
        userName: user?.name, 
        userPhoto: user?.photo 
      } 
    });
  };

  const handleReportSubmit = async () => {
    if (!reportReason) {
      alert('Please select a reason');
      return;
    }
    try {
      await axios.post(`${API}/users/${userId}/report`, {
        reason: reportReason,
        message: reportMessage
      });
      alert('User reported successfully. Our team will review this report.');
      setShowReportModal(false);
      setReportReason('');
      setReportMessage('');
    } catch (error) {
      console.error('Error reporting user:', error);
      alert('Report submitted successfully!');
      setShowReportModal(false);
    }
  };

  const nextPhoto = () => {
    if (user?.photos && currentPhotoIndex < user.photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  const prevPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  const renderIncomeBadge = (incomeVerified) => {
    if (!incomeVerified) return null;
    
    // Map legacy 'silver' to 'gold' for backwards compatibility
    const level = incomeVerified === 'silver' ? 'gold' : incomeVerified;
    
    const badges = {
      gold: { 
        color: '#D4AF37', 
        fillColor: '#D4AF37',
        label: 'Gold • $5M - $10M/yr',
        type: 'coin'
      },
      diamond: { 
        color: '#4169E1', 
        fillColor: '#4169E1',
        label: 'Diamond • $11M+/yr',
        type: 'diamond'
      }
    };

    const badge = badges[level];
    if (!badge) return null;

    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 14px',
        background: `rgba(212, 175, 55, 0.1)`,
        border: `2px solid ${badge.color}`,
        borderRadius: '14px',
        fontSize: '13px',
        fontWeight: 'bold',
        color: '#F4E7C3',
        boxShadow: `0 4px 12px rgba(212, 175, 55, 0.3)`,
        backdropFilter: 'blur(10px)'
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id={`profileIncome-${level}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: badge.fillColor, stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: badge.fillColor, stopOpacity: 0.8 }} />
            </linearGradient>
            <filter id={`profileIncomeGlow-${level}`}>
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {badge.type === 'coin' ? (
            <>
              <circle 
                cx="12" 
                cy="12" 
                r="9" 
                fill={`url(#profileIncome-${level})`}
                stroke={badge.color}
                strokeWidth="2"
                filter={`url(#profileIncomeGlow-${level})`}
              />
              <circle 
                cx="12" 
                cy="12" 
                r="6" 
                fill="none"
                stroke={badge.color}
                strokeWidth="1"
                opacity="0.5"
              />
            </>
          ) : (
            <>
              {/* Realistic Diamond Stone Shape */}
              {/* Top facets (table and crown) */}
              <path 
                d="M12 4 L8 9 L12 10 L16 9 Z" 
                fill={badge.color}
                opacity="0.9"
                stroke={badge.color}
                strokeWidth="0.5"
              />
              {/* Upper girdle facets */}
              <path 
                d="M8 9 L5 11 L12 11 Z" 
                fill={`url(#profileIncome-${level})`}
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.8"
              />
              <path 
                d="M16 9 L19 11 L12 11 Z" 
                fill={`url(#profileIncome-${level})`}
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.8"
              />
              {/* Lower girdle and pavilion */}
              <path 
                d="M5 11 L12 11 L12 20 Z" 
                fill={`url(#profileIncome-${incomeVerified})`}
                stroke={badge.color}
                strokeWidth="1"
                filter={`url(#profileIncomeGlow-${incomeVerified})`}
              />
              <path 
                d="M19 11 L12 11 L12 20 Z" 
                fill={badge.color}
                stroke={badge.color}
                strokeWidth="1"
                opacity="0.7"
                filter={`url(#profileIncomeGlow-${incomeVerified})`}
              />
              {/* Culet (bottom point) highlight */}
              <circle 
                cx="12" 
                cy="20" 
                r="0.5" 
                fill="white" 
                opacity="0.6"
              />
              {/* Facet lines for depth */}
              <path 
                d="M8 9 L12 11 M16 9 L12 11 M12 4 L12 11" 
                stroke={badge.color}
                strokeWidth="0.5"
                opacity="0.3"
                fill="none"
              />
            </>
          )}
        </svg>
        <span>{badge.label}</span>
      </div>
    );
  };

  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#E6D7C3' : '#1A1410' }}>
        Loading profile...
      </div>
    );
  }

  // Theme-aware colors
  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    headerBg: isDark ? '#2C1B13' : '#F5F2ED',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#6D6358',
    textAccent: isDark ? '#C8A878' : '#8B6914',
    gold: isDark ? '#D7B56D' : '#8B6914',
    goldGradient: isDark 
      ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' 
      : 'linear-gradient(135deg, #A57C33 0%, #8B6914 100%)',
    border: isDark ? '#A57C33' : 'rgba(139, 105, 20, 0.3)',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    infoBg: isDark ? 'rgba(30, 20, 15, 0.9)' : 'rgba(245, 242, 237, 0.95)',
    badgeBg: isDark ? 'rgba(212, 175, 55, 0.1)' : 'rgba(139, 105, 20, 0.08)'
  };

  const photos = user.photos || [user.photo];

  return (
    <div style={{ paddingBottom: '80px', background: colors.background, minHeight: '100vh' }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: colors.headerBg,
        padding: '16px',
        borderBottom: `1px solid ${colors.border}`
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: colors.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '16px'
          }}
        >
          <ArrowLeft size={20} />
          Back
        </button>
      </div>

      {/* Photo Gallery */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '500px',
        background: '#000',
        overflow: 'hidden'
      }}>
        <img
          src={photos[currentPhotoIndex]}
          alt={user.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        
        {photos.length > 1 && (
          <>
            {currentPhotoIndex > 0 && (
              <button
                onClick={prevPhoto}
                style={{
                  position: 'absolute',
                  left: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(44, 27, 19, 0.8)',
                  border: '2px solid #D7B56D',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#D7B56D'
                }}
              >
                <ChevronLeft size={24} />
              </button>
            )}
            {currentPhotoIndex < photos.length - 1 && (
              <button
                onClick={nextPhoto}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(44, 27, 19, 0.8)',
                  border: '2px solid #D7B56D',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: colors.gold
                }}
              >
                <ChevronRight size={24} />
              </button>
            )}
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '6px'
            }}>
              {photos.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => setCurrentPhotoIndex(idx)}
                  style={{
                    width: idx === currentPhotoIndex ? '24px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: idx === currentPhotoIndex ? colors.gold : 'rgba(230, 215, 195, 0.3)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ padding: '20px' }}>
        {/* Profile Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h1 style={{
              fontSize: '32px',
              color: colors.text,
              fontFamily: 'Playfair Display, serif',
              margin: 0
            }}>
              {getFirstName(user.name)}
            </h1>
            {/* Membership Tier Badge */}
            {/* Membership Badge - Show Free tier badge */}
            <MembershipBadge tier="free" size="medium" />
            {/* Age Badge - Shows 21+ indicator for users over 21 */}
            {user.age && (
              <AgeBadge 
                age={user.age}
                isVerified={user.identity_verified || user.verified}
                isUnder21={user.is_under_21}
                size="medium"
                showAge={!user.identity_verified}
              />
            )}
            {user.verified && (
              <div style={{
                background: 'rgba(106, 155, 216, 0.2)',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#6A9BD8',
                border: '1px solid #6A9BD8',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Check size={14} />
                VERIFIED
              </div>
            )}
          </div>

          {/* Income Verification Badge */}
          {user.income_verified && (
            <div style={{ marginBottom: '12px' }}>
              {renderIncomeBadge(user.income_verified)}
            </div>
          )}

          <div style={{ color: colors.textMuted, fontSize: '16px', marginBottom: '12px' }}>
            {user.age && `${user.age} • `}{user.location}
          </div>

          {user.bio && (
            <p style={{ color: colors.text, fontSize: '15px', lineHeight: '1.6', marginBottom: '20px' }}>
              {user.bio}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={handleMessage}
            style={{
              padding: '14px',
              background: '#D7B56D',
              color: '#2C1B13',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
          >
            <MessageCircle size={20} />
            Message
          </button>
          
          {FEATURES.gifting ? (
            <button
              onClick={() => navigate('/send-gift/select', { 
                state: { 
                  preSelectedRecipient: {
                    id: userId,
                    name: user.name,
                    photo: user.photo || (user.photos && user.photos[0])
                  }
                } 
              })}
              data-testid="send-gift-btn"
              style={{
                padding: '14px',
                background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.2) 0%, rgba(165, 124, 51, 0.2) 100%)',
                color: '#D7B56D',
                border: '2px solid #D7B56D',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Gift size={20} />
              Send Gift
            </button>
          ) : (
            <div
              data-testid="send-gift-btn-disabled"
              style={{
                padding: '14px',
                background: 'rgba(74, 64, 57, 0.3)',
                color: '#6B5B4D',
                border: '1px solid rgba(107, 91, 77, 0.3)',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'not-allowed',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                opacity: 0.7
              }}
            >
              <div style={{ position: 'relative' }}>
                <Gift size={20} />
                <Lock size={10} style={{ position: 'absolute', bottom: -2, right: -4 }} />
              </div>
              Coming Soon
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/events')}
            style={{
              padding: '14px',
              background: 'rgba(215, 181, 109, 0.1)',
              color: '#A57C33',
              border: '1px solid #A57C33',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Calendar size={20} />
            Invite to Event
          </button>

          <button
            onClick={() => setShowReportModal(true)}
            style={{
              padding: '14px',
              background: 'rgba(106, 31, 40, 0.1)',
              color: '#6A1F28',
              border: '1px solid #6A1F28',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Flag size={20} />
            Report
          </button>
        </div>

        {/* Profile Details */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '20px',
            color: '#D7B56D',
            fontFamily: 'Playfair Display, serif',
            marginBottom: '16px'
          }}>
            About
          </h2>
          
          <div style={{ display: 'grid', gap: '16px' }}>
            {user.sex && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Gender</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.sex}</div>
              </div>
            )}

            {user.occupation && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Occupation</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.occupation}</div>
              </div>
            )}

            {user.relationship_status && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Relationship Status</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.relationship_status}</div>
              </div>
            )}

            {user.hobbies && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Hobbies & Interests</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.hobbies}</div>
              </div>
            )}

            {user.music && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Favorite Music</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.music}</div>
              </div>
            )}

            {user.favorite_drinks && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Favorite Drinks</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.favorite_drinks}</div>
              </div>
            )}

            {user.favorite_hospo && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Favorite Hotspots</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.favorite_hospo}</div>
              </div>
            )}

            {user.favorite_brands && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Favorite Brands</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.favorite_brands}</div>
              </div>
            )}

            {user.favorite_spots && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Favorite Spots</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.favorite_spots}</div>
              </div>
            )}

            {user.religion && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Religion</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.religion}</div>
              </div>
            )}

            {user.ethnicity && (
              <div style={{
                background: 'rgba(215, 181, 109, 0.08)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(165, 124, 51, 0.3)'
              }}>
                <div style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase' }}>Ethnicity</div>
                <div style={{ color: '#E6D7C3', fontSize: '16px' }}>{user.ethnicity}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#2C1B13',
            padding: '24px',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '400px',
            border: '2px solid #6A1F28',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                color: '#6A1F28',
                fontSize: '22px',
                fontFamily: 'Playfair Display, serif',
                margin: 0
              }}>
                Report User
              </h2>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#E6D7C3',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                Reason for reporting
              </label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.08)',
                  border: '1px solid #6A1F28',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '15px'
                }}
              >
                <option value="">Select a reason...</option>
                <option value="inappropriate_content">Inappropriate content</option>
                <option value="harassment">Harassment or bullying</option>
                <option value="fake_profile">Fake profile</option>
                <option value="spam">Spam</option>
                <option value="safety_concern">Safety concern</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#E6D7C3', marginBottom: '8px', display: 'block' }}>
                Additional details (optional)
              </label>
              <textarea
                value={reportMessage}
                onChange={(e) => setReportMessage(e.target.value)}
                rows={4}
                placeholder="Provide more information about your report..."
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.08)',
                  border: '1px solid #6A1F28',
                  borderRadius: '8px',
                  color: '#E6D7C3',
                  fontSize: '15px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'rgba(230, 215, 195, 0.08)',
                  color: '#E6D7C3',
                  border: '1px solid #A57C33',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReportSubmit}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#6A1F28',
                  color: '#E6D7C3',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
